import L from "leaflet";
import type { Map } from "leaflet";

class Vector {
  constructor(start: L.Point, end: L.Point) {
    this.start = start;
    this.end = end;
  }

  start: L.Point;
  end: L.Point;

  getLength = (): number => {
    return Math.sqrt(
      Math.pow(this.start.x - this.end.x, 2) + Math.pow(this.start.y - this.end.y, 2),
    );
  };

  getAngle = (): number => {
    return Math.atan2(this.end.y - this.start.y, this.end.x - this.start.x);
  };

  getMidpoint = (): L.Point => {
    return new L.Point((this.start.x + this.end.x) / 2, (this.start.y + this.end.y) / 2);
  };
}

const getRectangle = (
  centerPoint: L.Point,
  width: number,
  height: number,
  rotationRadians: number,
): L.Point[] => {
  const halfWidth = width / 2;
  const halfHeight = height / 2;

  // Define the 4 corners relative to center before rotation
  const relativeCorners = [
    { x: -halfWidth, y: -halfHeight },
    { x: -halfWidth, y: halfHeight },
    { x: halfWidth, y: halfHeight },
    { x: halfWidth, y: -halfHeight },
  ];

  const cos = Math.cos(rotationRadians);
  const sin = Math.sin(rotationRadians);

  // Apply anti-clockwise rotation around the origin
  const rotatedCorners = relativeCorners.map((corner) => {
    return {
      x: corner.x * cos - corner.y * sin,
      y: corner.x * sin + corner.y * cos,
    };
  });

  // Move to the center point
  const corners = rotatedCorners.map((corner) => {
    return new L.Point(centerPoint.x + corner.x, centerPoint.y + corner.y);
  });

  return corners;
};

// Takes a parallelogram and returns a rectangle with right angles and positive width and height
export const enforceTrueRectangle = (map: Map, geometry: L.LatLng[]): L.LatLng[] => {
  if (!geometry || geometry.length !== 4) return geometry;

  // Convert LatLngs to Euclidean geometry
  const corners = geometry.map((g) => map.latLngToContainerPoint(g));

  // Variables are labelled assuming corners[0] is bottom-left and going clockwise,
  //   but this function works regardless of the order or orientation of the corners

  const vectorLeft = new Vector(corners[0], corners[1]);
  const vectorTop = new Vector(corners[1], corners[2]);
  const vectorRight = new Vector(corners[3], corners[2]);
  const vectorBottom = new Vector(corners[0], corners[3]);

  const midpointLeft = vectorLeft.getMidpoint();
  const midpointRight = vectorRight.getMidpoint();
  const midpointOfShape = new Vector(midpointLeft, midpointRight).getMidpoint();

  const lengthLeft = vectorLeft.getLength();
  const lengthBottom = vectorBottom.getLength();
  const internalAngle = vectorLeft.getAngle() - vectorBottom.getAngle();

  // Retain the angle and length of the longest sides, and adjust the angle and length of the shorter sides to be perpendicular
  const isTall = lengthLeft > lengthBottom;
  let width, angle, height: number;

  if (isTall) {
    angle = vectorLeft.getAngle() - Math.PI / 2;
    width = vectorBottom.getLength() * Math.sin(internalAngle);
    height = lengthLeft;
  } else {
    angle = vectorBottom.getAngle();
    width = lengthBottom;
    height = vectorLeft.getLength() * Math.sin(internalAngle);
  }

  if (Math.abs(width) < 1) width = width >= 0 ? 1 : -1;
  if (Math.abs(height) < 1) height = height >= 0 ? 1 : -1;

  const rectangle = getRectangle(midpointOfShape, width, height, angle);

  // Convert Euclidean geometry back to LatLngs
  const geoRectangle = rectangle.map((pt) => map.containerPointToLatLng(pt));
  return geoRectangle;
};
